/* eslint-disable react/forbid-prop-types */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Modal from '@ublend-npm/aulaui-next-web/Modal';

import CloseIcon from '@material-ui/icons/Close';
import TurnitinLogo from '../../../../../../static/turnitin.png';
import HandinLogo from '../../../../../../static/handin-logo.svg';
import getHandinUrl from '../utils/getHandinUrl';
import TurnitinModal from '../TurnitinModal';
import { isInstructorInSpace } from '../../../../../utils/user';
import {
  validateHandinAssignment,
  validateTurnItInAssignment,
  hasError,
} from './utils';
import step1Background from '../../../../../../static/create-assignment-2.jpg';
import successBackground from '../../../../../../static/create-assignment-3.svg';

import {
  Container,
  CloseButton,
  BackButtonPlaceholder,
  Header,
  Footer,
  SuccessFooter,
  Button,
  UnstyledButton,
  Stepper,
  Step,
  Content,
  Title,
  ModalTitle,
} from './CreateAssignmentModal.styled';

import ToolSelector from './ToolSelector';
import CreateModal from './CreateModal.component';
import { TURNITIN_PROVIDER, HANDIN_PROVIDER } from '../constants';
import gradeTypes from './gradeTypes';

const DEFAULT_STATE = {
  currentStep: 1,
  currentTool: 0,
  turnitinLaunch: null,
  saving: false,
  saved: false,
  editing: false,
  errors: {},
  hasAnonymizedError: false,
  assignment: {
    title: '',
    startDate: null,
    endDate: null,
    gradesReleasedAt: undefined,
    isHidden: true,
    shouldCloseOnDueDate: false,
    description: '',
    submissions: [],
    isAnonymised: false,
  },
};

const getModalContainer = () =>
  document.getElementById('user-profile-container');

/**
 * Due to the complex state implementation of this component, it must be reset for each use with a
 * React key. Consider this before attempting to implement React lifecycles.
 */
class CreateAssignmentModal extends PureComponent {
  constructor(props) {
    super(props);

    this.tools = [
      {
        logo: HandinLogo,
        name: 'Handin',
        key: HANDIN_PROVIDER,
        validator: validateHandinAssignment,
      },
    ];

    if (props.isTurnitinEnabled) {
      this.tools.push({
        logo: TurnitinLogo,
        name: 'Turnitin',
        key: TURNITIN_PROVIDER,
        validator: validateTurnItInAssignment,
      });
    }

    const currentSpaceValid = props.spaces.some(
      ({ objectId }) => objectId === props.currentSpace.objectId
    );
    this.state = {
      ...DEFAULT_STATE,
      // only set gradeType when tiiMaxGrade is enabled
      ...(props.tiiMaxGrade
        ? {
            assignment: {
              ...DEFAULT_STATE.assignment,
              gradeType: gradeTypes.percentage.value,
            },
          }
        : {}),
      currentSpace: currentSpaceValid ? props.currentSpace : props.spaces[0],
      // Jump to step 2 when editing (rather than creating) assignment
      ...(!!props.editingAssignment && {
        currentStep: 2,
        assignment: props.editingAssignment,
        editing: true,
        currentTool: this.tools
          .map((t) => t.key)
          .indexOf(props.editingAssignment.provider),
      }),
    };

    this.backgrounds = [step1Background];

    this.profileModal = React.createRef();

    // Babel bug means that async arrow functions as class members are not correctly bound.
    this.nextStep = this.nextStep.bind(this);
    this.onSpaceChange = this.onSpaceChange.bind(this);
    this.onToolChange = this.onToolChange.bind(this);
    this.getNextButtonContent = this.getNextButtonContent.bind(this);
    this.handleTextFieldChange = this.handleTextFieldChange.bind(this);
    this.handleSwitch = this.handleSwitch.bind(this);
    this.handleDateTimePicker = this.handleDateTimePicker.bind(this);
    this.handleGradeTypeChange = this.handleGradeTypeChange.bind(this);
    this.handleMaxGradeChange = this.handleMaxGradeChange.bind(this);
    this.launchTurnitin = this.launchTurnitin.bind(this);
    this.launchAssignment = this.launchAssignment.bind(this);
    this.prevStep = this.prevStep.bind(this);
    this.closeTurnItIn = this.closeTurnItIn.bind(this);
  }

  onSpaceChange(currentSpace) {
    this.setState({ currentSpace });
  }

  onToolChange(toolObj) {
    this.setState({
      currentTool: this.tools.map((t) => t.key).indexOf(toolObj.key),
    });
  }

  getNextButtonContent() {
    const { currentStep } = this.state;

    return currentStep === 2 ? 'Save' : 'Next';
  }

  handleTextFieldChange(name) {
    return (event) => {
      const { assignment, errors } = this.state;
      const { value } = event.target;

      this.setState({
        errors: {
          ...errors,
          [name]: false,
        },
        assignment: {
          ...assignment,
          [name]: value,
        },
      });
    };
  }

  handleSwitch(name) {
    return () => {
      const { assignment } = this.state;
      const status = assignment[name];

      this.setState({
        assignment: {
          ...assignment,
          [name]: !status,
        },
      });
    };
  }

  handleDateTimePicker(name) {
    return (date) => {
      const { assignment, errors } = this.state;

      this.setState({
        errors: {
          ...errors,
          [name]: false,
        },
        assignment: {
          ...assignment,
          [name]: date,
        },
      });
    };
  }

  handleGradeTypeChange({ target: { value } }) {
    const { tiiMaxGrade } = this.props;
    const { assignment, errors } = this.state;

    // use institution default value if user has not set yet
    const maxGrade = assignment.maxGrade || tiiMaxGrade;

    this.setState({
      errors: {
        ...errors,
        gradeType: false,
      },
      assignment: {
        ...assignment,
        gradeType: value,
        ...(value === gradeTypes.points.value
          ? { maxGrade } // set maxGrade property for points grade
          : { maxGrade: undefined }), // unset maxGrade when switched to percentages
      },
    });
  }

  handleMaxGradeChange({ target: { value } }) {
    const { assignment, errors } = this.state;
    const maxGrade = parseInt(value, 10);

    this.setState({
      errors: {
        ...errors,
        maxGrade: false,
      },
      assignment: {
        ...assignment,
        maxGrade,
      },
    });
  }

  launchTurnitin(assignment) {
    this.setState({
      turnitinLaunch: assignment,
    });
  }

  closeTurnItIn() {
    this.setState({
      turnitinLaunch: null,
    });
  }

  launchAssignment(assignment) {
    if (assignment.provider === TURNITIN_PROVIDER) {
      this.launchTurnitin(assignment);
    }
  }

  prevStep() {
    const { currentStep } = this.state;

    if (currentStep > 1) {
      this.setState({
        currentStep: currentStep - 1,
      });
    }
  }

  async nextStep() {
    const {
      currentStep,
      currentTool,
      currentSpace: { objectId: space },
    } = this.state;
    const { baseDomain } = this.props;

    const shouldRedirectToHandin =
      currentStep === 1 && this.tools[currentTool].key === HANDIN_PROVIDER;

    if (shouldRedirectToHandin) {
      window.location.href = getHandinUrl({
        baseDomain,
        space,
        search: 'createAssignment=true&source=ma',
      });
      return;
    }

    if (currentStep < 2) {
      this.setState({
        currentStep: currentStep + 1,
      });
    } else {
      try {
        this.setState({ saving: true });
        const newAssignment = await this.save();
        if (newAssignment) {
          this.setState({
            ...DEFAULT_STATE,
            currentTool,
            currentStep: 2,
            saving: false,
            saved: true,
            newAssignment,
          });

          this.launchAssignment(newAssignment);
        } else {
          this.setState({ saving: false });
        }
      } catch (error) {
        const hasAnonymizedError =
          error &&
          error.message &&
          error.message === 'cannot-update-anonymous-status';
        this.setState({
          ...DEFAULT_STATE,
          currentTool,
          currentStep: 2,
          saving: false,
          hasAnonymizedError,
        });
      }
    }
  }

  async save() {
    const { currentTool, currentSpace, assignment, editing } = this.state;
    const {
      user,
      createAssignment,
      editAssignment,
      fetchAccessToken,
    } = this.props;
    const { key: provider, validator } = this.tools[currentTool];
    const errors = validator(assignment);

    if (hasError(errors)) {
      this.setState({ errors });
      return false;
    }

    const accessToken = await fetchAccessToken(currentSpace.objectId);
    const editOrCreate = editing ? editAssignment : createAssignment;

    return editOrCreate(user, provider, currentSpace, assignment, accessToken);
  }

  renderStepOne() {
    const { currentTool } = this.state;

    return (
      <React.Fragment>
        <Title>Select a tool</Title>
        <ToolSelector
          compact
          onToolChange={this.onToolChange}
          currentTool={this.tools[currentTool]}
          tools={this.tools}
        />
      </React.Fragment>
    );
  }

  renderStepTwo() {
    const { tiiMaxGrade } = this.props;

    const {
      currentSpace,
      assignment,
      currentTool,
      saving,
      editing,
      saved,
      errors,
      hasAnonymizedError,
    } = this.state;

    return (
      <CreateModal
        space={currentSpace.objectId}
        handleChange={this.handleTextFieldChange}
        handleDateTimePicker={this.handleDateTimePicker}
        handleGradeTypeChange={this.handleGradeTypeChange}
        handleMaxGradeChange={this.handleMaxGradeChange}
        tiiMaxGrade={tiiMaxGrade}
        handleSwitch={this.handleSwitch}
        assignment={assignment}
        hasAnonymizedError={hasAnonymizedError}
        provider={this.tools[currentTool].key}
        isSaving={saving}
        isEditing={editing}
        done={saved}
        errors={errors}
      />
    );
  }

  render() {
    const { onClose, isVisible, user } = this.props;

    const {
      currentStep,
      editing,
      saved,
      turnitinLaunch,
      newAssignment,
      currentSpace,
    } = this.state;

    const background = saved
      ? successBackground
      : this.backgrounds[currentStep - 1];
    const shouldShowBackButton = currentStep !== 1 && !editing;
    const nextButton = this.getNextButtonContent();
    const isEducator = currentSpace
      ? isInstructorInSpace(user.objectId, currentSpace.objectId)
      : false;
    const modalId = `user-profile-modal_${
      !currentSpace ? user.objectId : currentSpace.objectId
    }`;
    const title = editing ? 'Edit assignment' : 'New assignment';

    return (
      <Modal
        id={modalId}
        container={getModalContainer}
        open={isVisible}
        onClose={onClose}
      >
        <Container background={background}>
          <Header>
            <CloseButton
              onClick={onClose}
              role="button"
              tabIndex={0}
              aria-label="Close create assignment dialog."
              onKeyPress={(e) => {
                if (e.key === 'Enter') onClose();
              }}
            >
              <CloseIcon />
            </CloseButton>
            <ModalTitle>{title}</ModalTitle>
          </Header>
          <Content createstep={currentStep === 2} compact={saved}>
            {currentStep === 1 && this.renderStepOne()}
            {currentStep === 2 && this.renderStepTwo()}
          </Content>
          {!saved && (
            <Footer>
              {shouldShowBackButton && (
                <Button onClick={this.prevStep} aria-label="Back">
                  Back
                </Button>
              )}
              {!shouldShowBackButton && <BackButtonPlaceholder />}
              <Stepper>
                <Step
                  active={currentStep >= 1}
                  role="button"
                  tabIndex={currentStep >= 1 ? '0' : '-1'}
                  aria-label="Step 1 from 2"
                />
                <Step
                  active={currentStep >= 2}
                  role="button"
                  tabIndex={currentStep >= 2 ? '0' : '-1'}
                  aria-label="Step 2 from 2"
                />
              </Stepper>
              <Button
                primary
                onClick={this.nextStep}
                aria-label={nextButton}
                tabIndex={0}
                data-testid="next-assignment-create-step"
              >
                {nextButton}
              </Button>
            </Footer>
          )}
          {saved && (
            <SuccessFooter>
              <Button
                primary
                onClick={() => this.launchAssignment(newAssignment)}
                aria-label="View Assignment"
              >
                View Assignment
              </Button>
              <UnstyledButton
                onClick={onClose}
                aria-label="Close"
                data-testid="close-assignment-create-button"
              >
                Close
              </UnstyledButton>
            </SuccessFooter>
          )}
        </Container>
        {turnitinLaunch && (
          <TurnitinModal
            key={turnitinLaunch}
            user={user}
            assignment={turnitinLaunch}
            space={currentSpace}
            isEducator={isEducator}
            onClose={this.closeTurnItIn}
          />
        )}
      </Modal>
    );
  }
}

CreateAssignmentModal.defaultProps = {
  tiiMaxGrade: undefined,
};

CreateAssignmentModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentSpace: PropTypes.object.isRequired,
  editingAssignment: PropTypes.object,
  isTurnitinEnabled: PropTypes.bool.isRequired,
  tiiMaxGrade: PropTypes.number,
  user: PropTypes.object.isRequired,
  spaces: PropTypes.array.isRequired,
  createAssignment: PropTypes.func.isRequired,
  editAssignment: PropTypes.func.isRequired,
  fetchAccessToken: PropTypes.func.isRequired,
  baseDomain: PropTypes.string.isRequired,
};

CreateAssignmentModal.defaultProps = {
  editingAssignment: null,
};

export default CreateAssignmentModal;
